import {ReadonlyURLSearchParams} from 'next/navigation';

import {ConfigSchema, OmicronConfigResponseType} from './types';

const extractConfigOverridesFromQuery = (
  params: ReadonlyURLSearchParams | null,
  configSchema: ConfigSchema,
): Record<string, unknown> | null => {
  if (!params || params.size === 0) {
    return null;
  }
  return Object.entries(configSchema).reduce(
    (
      acc,
      [
        key,
        {
          value: {is, fallback},
        },
      ],
    ) => {
      const value = params.get(key);
      if (!value) {
        return acc;
      }
      if (typeof fallback === 'boolean') {
        if (['true', 'false'].includes(value.toLowerCase())) {
          acc[key] = value.toLowerCase() === 'true';
        } else {
          console.warn(
            `Incorrect type of config override for key ${key}, expected boolean`,
            value,
          );
        }
      } else if (typeof fallback === 'string') {
        // run is to check for string union types
        if ((is as (v: unknown) => unknown)(value as unknown)) {
          acc[key] = value;
        } else {
          console.warn(`Incorrect string union value provided for key ${key}`);
        }
      } else if (typeof fallback === 'number') {
        const numValue = Number.parseInt(value);
        if (!isNaN(numValue)) {
          acc[key] = numValue;
        } else {
          console.warn(`Incorrect numeric value provided for key ${key}`);
        }
      } else if (typeof fallback === 'object') {
        try {
          const obj = JSON.parse(value);
          if (is(obj)) {
            acc[key] = obj;
          } else {
            console.warn(`Object failed validation for ${key}`, obj);
          }
        } catch (e) {
          console.warn(`Failed to parse ${key} = ${value}`);
        }
      } else {
        console.warn(
          `Unsupported type of config override for key ${key}`,
          typeof fallback,
        );
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );
};

export const applyOmicronConfigOverridesFromQuery = <
  ConfigType extends Record<string, unknown>,
>({
  omicronResponse,
  searchParams,
  schema,
}: {
  omicronResponse: OmicronConfigResponseType<ConfigType>;
  searchParams: ReadonlyURLSearchParams | null;
  schema: ConfigSchema;
}) => {
  const configPatch = extractConfigOverridesFromQuery(searchParams, schema);
  if (configPatch && Object.keys(configPatch).length) {
    console.warn('Patching omicron ab config: ', configPatch);
    omicronResponse.config = {
      ...omicronResponse.config,
      ...configPatch,
    } as ConfigType;
  }
};
